import { useRef, useState } from 'react';
import { Play, Pause, UploadSimple, Trash, SpeakerHigh } from '@phosphor-icons/react';
import { Button } from './ui/button';
import api, { getErrorMessage } from '../utils/api';
import { imgUrl } from '../utils/imgUrl';
import { toast } from 'sonner';

const fmt = (s) => {
  if (!isFinite(s)) return '0:00';
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
};

// Inline audio player for a call log; also lets staff attach / replace / remove a recording
export const CallRecordingPlayer = ({ log, canManage = true, onChanged }) => {
  const audioRef = useRef(null);
  const fileRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRate] = useState(1);
  const [busy, setBusy] = useState(false);

  const url = log?.call_recording_url ? imgUrl(log.call_recording_url) : null;

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); }
  };

  const cycleRate = () => {
    const next = rate === 1 ? 1.5 : rate === 1.5 ? 2 : 1;
    setRate(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  const upload = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    try {
      setBusy(true);
      await api.post(`/notifications/call-logs/${log.log_id}/recording`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Recording attached');
      onChanged?.();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to upload recording'));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = async () => {
    if (!window.confirm('Remove this recording?')) return;
    try {
      setBusy(true);
      await api.delete(`/notifications/call-logs/${log.log_id}/recording`);
      toast.success('Recording removed');
      onChanged?.();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to remove recording'));
    } finally {
      setBusy(false);
    }
  };

  if (!log?.log_id) return null;

  return (
    <div className="mt-3 pt-3 border-t border-[#E5E5DF]" data-testid={`call-recording-${log.log_id}`}>
      {url ? (
        <div className="flex items-center gap-3 rounded-lg bg-[#F4F6F4] border border-[#E5E5DF] px-3 py-2">
          <audio
            ref={audioRef}
            src={url}
            preload="metadata"
            onLoadedMetadata={(e) => setDuration(e.target.duration)}
            onTimeUpdate={(e) => setTime(e.target.currentTime)}
            onEnded={() => setPlaying(false)}
          />
          <button type="button" data-testid={`recording-play-${log.log_id}`} onClick={toggle}
            className="w-9 h-9 rounded-full bg-[#2C4C3B] text-white flex items-center justify-center hover:bg-[#1F3529] shrink-0">
            {playing ? <Pause size={16} weight="fill" /> : <Play size={16} weight="fill" />}
          </button>
          <div className="flex-1 min-w-0">
            <input
              type="range" min={0} max={duration || 0} step={0.1} value={time}
              onChange={(e) => { const v = Number(e.target.value); setTime(v); if (audioRef.current) audioRef.current.currentTime = v; }}
              className="w-full accent-[#2C4C3B] h-1.5"
              aria-label="Seek"
            />
            <div className="flex items-center justify-between text-[11px] text-[#1C1C1A]/60 mt-0.5">
              <span>{fmt(time)} / {fmt(duration)}</span>
              <span className="truncate ml-2">
                {log.recording_source === 'staff_upload' ? `Uploaded by ${log.recording_uploaded_by || 'staff'}` : 'AI call recording'}
              </span>
            </div>
          </div>
          <button type="button" onClick={cycleRate} data-testid={`recording-speed-${log.log_id}`}
            className="text-xs font-semibold text-[#2C4C3B] border border-[#2C4C3B]/30 rounded px-1.5 py-0.5 hover:bg-white shrink-0">
            {rate}x
          </button>
          {canManage && (
            <button type="button" onClick={remove} disabled={busy} title="Remove recording" data-testid={`recording-delete-${log.log_id}`}
              className="text-red-600 hover:text-red-800 shrink-0">
              <Trash size={16} />
            </button>
          )}
        </div>
      ) : canManage ? (
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept="audio/*,.m4a,.amr,.aac,.mp3,.wav,.ogg,.3gp" className="hidden"
            data-testid={`recording-file-input-${log.log_id}`} onChange={(e) => upload(e.target.files?.[0])} />
          <Button size="sm" variant="outline" disabled={busy} data-testid={`recording-upload-btn-${log.log_id}`}
            onClick={() => fileRef.current?.click()} className="border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white h-8">
            <UploadSimple size={14} className="mr-1.5" />
            {busy ? 'Uploading…' : 'Attach call recording'}
          </Button>
          <span className="text-[11px] text-[#1C1C1A]/50 inline-flex items-center gap-1">
            <SpeakerHigh size={12} /> From your phone's call recorder (mp3, m4a, amr…)
          </span>
        </div>
      ) : null}
    </div>
  );
};
